import { useState } from "react";
import {
  Loader2,
  AlertCircle,
  FileSpreadsheet,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useFiscalYears } from "@/features/fiscal-calendar";
import {
  useIncomeStatement,
  type IncomeStatementSection,
} from "@/features/reports";

/**
 * Format a date string for display.
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString + "T00:00:00");
  return date.toLocaleDateString("es-CL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/**
 * Format a number as currency.
 */
function formatCurrency(value: string | number): string {
  const num = typeof value === "string" ? parseFloat(value) : value;
  return num.toLocaleString("es-CL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Income Statement section component.
 */
function IncomeStatementSectionView({
  section,
  isExpense = false,
}: {
  section: IncomeStatementSection;
  isExpense?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(true);
  const hasItems = section.items.length > 0;
  const total = parseFloat(section.total);

  // Skip rendering if no items and zero total
  if (!hasItems && total === 0) {
    return null;
  }

  return (
    <div className="border-b last:border-0">
      {/* Section header */}
      <button
        className="w-full flex items-center justify-between px-4 py-2 hover:bg-muted/50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
        disabled={!hasItems}
      >
        <div className="flex items-center gap-2">
          {hasItems ? (
            isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )
          ) : (
            <div className="w-4" />
          )}
          <span className="font-medium">{section.label}</span>
        </div>
        <span
          className={cn("font-mono", isExpense && total > 0 && "text-red-600")}
        >
          {isExpense && total > 0 ? "(" : ""}${formatCurrency(Math.abs(total))}
          {isExpense && total > 0 ? ")" : ""}
        </span>
      </button>

      {/* Items (when expanded) */}
      {isExpanded && hasItems && (
        <div className="bg-muted/20">
          {section.items.map((item) => (
            <div
              key={item.accountId}
              className={cn(
                "flex items-center justify-between px-4 py-1.5 text-sm",
                item.isHeader && "font-medium text-muted-foreground",
              )}
              style={{ paddingLeft: `${(item.level + 2) * 16}px` }}
            >
              <span>
                {item.accountCode} - {item.accountName}
              </span>
              {!item.isHeader && (
                <span className="font-mono">
                  ${formatCurrency(item.balance)}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Summary line component.
 */
function SummaryLine({
  label,
  value,
  isTotal = false,
  isNegative = false,
}: {
  label: string;
  value: string;
  isTotal?: boolean;
  isNegative?: boolean;
}) {
  const num = parseFloat(value);
  const showNegative = isNegative || num < 0;

  return (
    <div
      className={cn(
        "flex items-center justify-between px-4 py-2",
        isTotal ? "bg-primary/10 font-semibold" : "bg-muted/30",
      )}
    >
      <span>{label}</span>
      <span className={cn("font-mono", showNegative && "text-red-600")}>
        {showNegative ? "(" : ""}${formatCurrency(Math.abs(num))}
        {showNegative ? ")" : ""}
      </span>
    </div>
  );
}

/**
 * Estado de Resultados (Income Statement) page.
 */
export function EstadoResultadosPage() {
  // Fiscal year selector state
  const [selectedFiscalYearId, setSelectedFiscalYearId] = useState<string>("");
  const [includeZeroBalances, setIncludeZeroBalances] = useState(false);

  // Fetch fiscal years for selector
  const { years: fiscalYears, isLoading: fiscalYearsLoading } =
    useFiscalYears();

  // Fetch income statement
  const {
    report,
    isLoading: reportLoading,
    error: reportError,
  } = useIncomeStatement({
    fiscalYearId: selectedFiscalYearId || undefined,
    includeZeroBalances,
  });

  // Export to Excel placeholder
  const handleExport = () => {
    alert("Exportar a Excel - Proximamente");
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Estado de Resultados</h1>
          <p className="text-sm text-muted-foreground">
            Estado de perdidas y ganancias
          </p>
        </div>
        {report && (
          <Button variant="outline" onClick={handleExport}>
            <FileSpreadsheet className="mr-2 h-4 w-4" />
            Exportar
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-end">
        {/* Fiscal year selector */}
        <div className="w-[200px]">
          <label className="text-sm font-medium mb-1 block">Ano Fiscal</label>
          <Select
            value={selectedFiscalYearId}
            onValueChange={setSelectedFiscalYearId}
            disabled={fiscalYearsLoading}
          >
            <SelectTrigger>
              <SelectValue placeholder="Seleccionar..." />
            </SelectTrigger>
            <SelectContent>
              {fiscalYears.map((fy) => (
                <SelectItem key={fy.id} value={fy.id}>
                  {fy.yearNumber}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Include zero balances */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="includeZeroBalances"
            checked={includeZeroBalances}
            onChange={(e) => setIncludeZeroBalances(e.target.checked)}
            className="rounded border-gray-300"
          />
          <label htmlFor="includeZeroBalances" className="text-sm">
            Incluir cuentas sin saldo
          </label>
        </div>
      </div>

      {/* Content */}
      {!selectedFiscalYearId ? (
        // No fiscal year selected
        <div className="border rounded-lg p-12 bg-card flex flex-col items-center justify-center text-center">
          <FileSpreadsheet className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">Seleccione un ano fiscal</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            Elija un ano fiscal para generar el Estado de Resultados del
            periodo.
          </p>
        </div>
      ) : reportLoading ? (
        // Loading state
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : reportError ? (
        // Error state
        <div className="flex flex-col items-center justify-center h-64 gap-2 text-destructive">
          <AlertCircle className="h-8 w-8" />
          <p>Error al generar el reporte</p>
          <p className="text-sm text-muted-foreground">{reportError.message}</p>
        </div>
      ) : report ? (
        // Income Statement report
        <div className="space-y-4">
          {/* Report header */}
          <div className="border rounded-lg bg-card p-4">
            <div>
              <h2 className="text-lg font-semibold">
                {report.organizationName}
              </h2>
              <p className="text-sm text-muted-foreground">
                Estado de Resultados del {formatDate(report.periodStart)} al{" "}
                {formatDate(report.periodEnd)}
              </p>
              <p className="text-xs text-muted-foreground">
                Ano Fiscal {report.fiscalYear.yearNumber} | Moneda:{" "}
                {report.currency}
              </p>
            </div>
          </div>

          {/* Income Statement sections */}
          <div className="border rounded-lg bg-card overflow-hidden">
            {/* Operating Revenue */}
            <div className="bg-primary/10 px-4 py-2 font-semibold text-primary">
              Ingresos
            </div>
            <IncomeStatementSectionView section={report.operatingRevenue} />
            <IncomeStatementSectionView section={report.otherRevenue} />

            {/* Cost of Goods Sold */}
            <div className="bg-primary/10 px-4 py-2 font-semibold text-primary mt-2">
              Costos
            </div>
            <IncomeStatementSectionView
              section={report.costOfGoodsSold}
              isExpense
            />

            {/* Gross Profit */}
            <SummaryLine label="Utilidad Bruta" value={report.grossProfit} />

            {/* Operating Expenses */}
            <div className="bg-primary/10 px-4 py-2 font-semibold text-primary mt-2">
              Gastos
            </div>
            <IncomeStatementSectionView
              section={report.operatingExpenses}
              isExpense
            />
            <IncomeStatementSectionView
              section={report.otherExpenses}
              isExpense
            />

            {/* Operating Income */}
            <SummaryLine
              label="Resultado Operacional"
              value={report.operatingIncome}
            />

            {/* Income Before Taxes */}
            <SummaryLine
              label="Resultado Antes de Impuestos"
              value={report.incomeBeforeTaxes}
            />

            {/* Tax Expense */}
            {parseFloat(report.incomeTaxExpense) !== 0 && (
              <IncomeStatementSectionView
                section={report.taxExpense}
                isExpense
              />
            )}

            {/* Net Income */}
            <SummaryLine
              label="Resultado del Ejercicio"
              value={report.netIncome}
              isTotal
            />
          </div>

          {/* Summary box */}
          <div className="border rounded-lg bg-card p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-muted/30 rounded">
                <p className="text-xs text-muted-foreground mb-1">
                  Ingresos Totales
                </p>
                <p className="font-mono font-semibold">
                  $
                  {formatCurrency(
                    parseFloat(report.totalOperatingRevenue) +
                      parseFloat(report.otherRevenue.total),
                  )}
                </p>
              </div>
              <div className="text-center p-3 bg-muted/30 rounded">
                <p className="text-xs text-muted-foreground mb-1">
                  Utilidad Bruta
                </p>
                <p className="font-mono font-semibold">
                  ${formatCurrency(report.grossProfit)}
                </p>
              </div>
              <div className="text-center p-3 bg-muted/30 rounded">
                <p className="text-xs text-muted-foreground mb-1">
                  Resultado Operacional
                </p>
                <p className="font-mono font-semibold">
                  ${formatCurrency(report.operatingIncome)}
                </p>
              </div>
              <div className="text-center p-3 bg-primary/10 rounded">
                <p className="text-xs text-muted-foreground mb-1">
                  Resultado Neto
                </p>
                <p
                  className={cn(
                    "font-mono font-semibold",
                    parseFloat(report.netIncome) < 0 && "text-red-600",
                  )}
                >
                  ${formatCurrency(report.netIncome)}
                </p>
              </div>
            </div>
          </div>

          {/* Generated timestamp */}
          <p className="text-xs text-muted-foreground text-center">
            Generado el {new Date(report.generatedAt).toLocaleString("es-CL")}
          </p>
        </div>
      ) : null}
    </div>
  );
}
